<script setup lang="ts">
import { Field } from 'vee-validate'

import CardTitle from '@/components/CardTitle.vue'
import InputCaracterCounter from '@/components/InputCaracterCounter.vue'
import LoyaltyPointExpirationSettings from './LoyaltyPointExpirationSettings.vue'
import type { LoyaltyPointsConfigForm, LoyaltyPointsConfigField } from '@/types/types'

const { t } = useI18n()
const { scrollToBlockBottom } = useDOM()
const { formatFloatAndIntegerKeys } = useNumber()
const { isSupportConnection } = useLocalStorage()

const tooltipText = computed(() =>
  t('pages.settings.merchantForm.config.pointsLabelTooltip', {
    pointsLabel: `"points"`
  })
)

const handleKeyDown = (e: KeyboardEvent, field: LoyaltyPointsConfigField) => {
  if (field.type === 'text') {
    return
  }

  formatFloatAndIntegerKeys({ e, isIntegerOnly: field.integerOnly, type: field.type })
}

defineProps<{
  loyaltyPointsConfigFields: LoyaltyPointsConfigForm
  pointExpirationStartsAt?: string | null
}>()

const pointExpirationType = defineModel<'point_relative' | 'after_registration' | 'absolute_date' | null>(
  'pointExpirationType',
  { default: null }
)
const pointExpirationUnit = defineModel<'day' | 'month' | 'year' | null>('pointExpirationUnit', { default: null })
const pointExpirationValue = defineModel<number | string | null>('pointExpirationValue', { default: null })

const getVisibleFields = (fields: Record<any, LoyaltyPointsConfigField>) => {
  return Object.entries(fields).filter(([_, field]) => !field.isSupportConnectionOnly || isSupportConnection.value)
}
</script>

<template>
  <div class="space-y-5">
    <div ref="container" class="rounded-lg border bg-white pb-5">
      <div class="border-b p-5">
        <CardTitle :title="t('pages.loyaltyPage.tabs.points.sectionTitle')" />
      </div>

      <div
        v-for="sections in Object.values(loyaltyPointsConfigFields)"
        :key="sections.title"
        class="mx-5 mt-5 divide-y rounded-md border bg-white"
      >
        <div class="flex flex-col rounded-md border-b bg-white p-5">
          <div class="flex flex-row items-center justify-between">
            <p class="text-sm font-medium text-foreground">{{ sections.title }}</p>
            <BaseSwitch
              v-if="sections.hasToggle"
              :id="sections.title"
              v-model="sections.toggleValue"
              :is-disabled="sections.toggleDisabled"
              @click="sections.toggleValue && scrollToBlockBottom($refs.container as HTMLElement)"
            />
          </div>
          <div v-if="sections.toggleTips" class="mt-2 flex items-center gap-1 text-xs text-amber-600">
            <BaseIcon name="IconoirInfoCircle" class="h-4 w-4" />
            <span
              >{{ sections.toggleTips.text }}
              <router-link :to="sections.toggleTips.link.value" class="underline">{{
                t('pages.loyaltyPage.tabs.points.integrationLink')
              }}</router-link>
            </span>
          </div>
        </div>
        <div
          v-if="!sections.hasToggle || sections.toggleValue"
          class="grid grid-cols-2 gap-5 rounded-b-md bg-white p-5"
        >
          <div
            v-for="[fieldKey, field] in getVisibleFields(sections.fields)"
            :key="field.label"
            class="flex flex-col gap-1"
          >
            <div v-if="field.type === 'toggle'" class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <BaseSupportConnection :model-value="field.isSupportConnectionOnly">
                  <label :for="fieldKey" class="max-w-max text-sm font-medium text-foreground">
                    {{ field.label }}
                  </label>
                </BaseSupportConnection>

                <AppTooltip v-if="field.toolTip" tooltip-position="right" :text="field.toolTip" width="w-[310px]">
                  <BaseIcon name="IconoirInfoCircle" class="h-4 w-4 cursor-pointer text-foreground-secondary" />
                </AppTooltip>
              </div>
              <BaseSwitch :id="fieldKey" v-model="field.value" />
            </div>
            <div v-else>
              <div class="flex items-center gap-1">
                <label :for="field.label" class="max-w-max text-sm font-medium text-foreground">
                  {{ field.label }}<sup v-if="field.required" class="ml-1 text-red-600">*</sup>
                </label>
                <AppTooltip v-if="field.hasToolTip" tooltip-position="right" :text="tooltipText" width="w-[310px]">
                  <BaseIcon name="IconoirInfoCircle" class="h-4 w-4 cursor-pointer text-foreground-secondary" />
                </AppTooltip>
              </div>
              <Field
                v-slot="{ value, handleChange, handleBlur }"
                v-model="field.value"
                :name="field.label"
                :rules="field.rules"
              >
                <Input
                  :id="field.label"
                  :type="field.type"
                  :required="field.required"
                  :disabled="field.disabled"
                  :model-value="value"
                  @update:model-value="handleChange"
                  @blur="handleBlur"
                  @keydown="handleKeyDown($event, field)"
                />
              </Field>
              <InputCaracterCounter
                v-if="field.maxLength"
                :content="field.value.toString()"
                :max-length="field.maxLength"
                @truncate-content="field.value = $event"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <LoyaltyPointExpirationSettings
      v-model:point-expiration-type="pointExpirationType"
      v-model:point-expiration-unit="pointExpirationUnit"
      v-model:point-expiration-value="pointExpirationValue"
      :point-expiration-starts-at="pointExpirationStartsAt"
    />
  </div>
</template>
